import * as React from "react";
import {
  ArrowUpDownIcon,
  CheckIcon,
  LayersIcon,
  PlusIcon,
  XIcon,
} from "lucide-react";

import { cn } from "../../lib/utils";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from "./select";
import { menuTriggerClass } from "./trigger-recipes";

/** The 36px inline toolbar that replaces big section headers: filter chips, grouping, ordering, view. */

/* ── FilterBar ──────────────────────────────────────────────────────────── */

export interface FilterBarProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> {
  /** Controls that stay put — search field, filter trigger — rendered left-most. */
  filters?: React.ReactNode;
  /**
   * Active `FilterChip`s. They get their own row *below* the toolbar so the
   * controls above never shift sideways as chips come and go; the row collapses
   * to nothing (`empty:hidden`) while no filter is set.
   */
  activeFilters?: React.ReactNode;
  /** Shows the "+ Filter" affordance when provided. */
  onAddFilter?: () => void;
  /** `GroupByControl` (right side). */
  groupBy?: React.ReactNode;
  /** `OrderByControl` (right side). */
  orderBy?: React.ReactNode;
  /** Segmented view switcher (right side). */
  view?: React.ReactNode;
  /** Result count, rendered as tertiary tabular meta before the right controls. */
  count?: number;
  /** Extra left-side content, after the filters. */
  children?: React.ReactNode;
}

const FilterBar = React.forwardRef<HTMLDivElement, FilterBarProps>(
  (
    {
      filters,
      activeFilters,
      onAddFilter,
      groupBy,
      orderBy,
      view,
      count,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const hasRight = count !== undefined || Boolean(groupBy || orderBy || view);

    return (
      <div
        ref={ref}
        data-slot="filter-bar"
        className={cn(
          // Stays on the canvas tier: the bar is chrome above a raised content
          // sheet, so the tier step below it is the separation.
          "flex shrink-0 flex-col bg-page",
          className,
        )}
        {...props}
      >
        <div
          data-slot="filter-bar-controls"
          role="toolbar"
          aria-label="Filters and view options"
          className={cn(
            "flex h-toolbar shrink-0 items-center gap-1 px-4",
            "overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          )}
        >
          {filters}
          {onAddFilter ? <AddFilterButton onClick={onAddFilter} /> : null}
          {children}
          {hasRight ? (
            <div className="ml-auto flex shrink-0 items-center gap-1">
              {count !== undefined ? (
                <span className="mr-1 text-xs tabular-nums text-text-tertiary">
                  {count}
                </span>
              ) : null}
              {groupBy}
              {orderBy}
              {view}
            </div>
          ) : null}
        </div>
        {/*
          `empty:hidden` is what keeps this free: an `ActiveFilters` component
          that renders `null` leaves no child nodes, so the row takes no height
          and the page below does not move when the last chip is removed.
        */}
        <div
          data-slot="filter-bar-active"
          className="flex flex-wrap items-center gap-1 px-4 pb-1 empty:hidden"
        >
          {activeFilters}
        </div>
      </div>
    );
  },
);
FilterBar.displayName = "FilterBar";

/* ── AddFilterButton ────────────────────────────────────────────────────── */

export interface AddFilterButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string;
}

/**
 * The "+ Filter" affordance. Rendered by `FilterBar` when `onAddFilter` is set.
 *
 * Uses the shared Job C toolbar recipe (`docs/picker-taxonomy.md` §2.7), which
 * adds the `data-[state=open]` pair this was missing: without it the button
 * renders at rest while its own panel is open.
 */
const AddFilterButton = React.forwardRef<
  HTMLButtonElement,
  AddFilterButtonProps
>(({ label = "Filter", className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    data-slot="add-filter"
    className={menuTriggerClass("compact", className)}
    {...props}
  >
    <PlusIcon aria-hidden="true" className="size-3" />
    {label}
  </button>
));
AddFilterButton.displayName = "AddFilterButton";

/* ── FilterChip ─────────────────────────────────────────────────────────── */

export interface FilterChipProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onClick"
> {
  /** The field being filtered, e.g. "Status". */
  label: string;
  /** "is" | "is not" | "contains" — omitted renders label and value adjacent. */
  operator?: string;
  /** The current value, e.g. "Active" or "3 selected". */
  value: string;
  icon?: React.ReactNode;
  /** Shows the trailing remove affordance. */
  onRemove?: () => void;
  /** Opens the value popover / menu. */
  onClick?: () => void;
}

const FilterChip = React.forwardRef<HTMLDivElement, FilterChipProps>(
  (
    { label, operator, value, icon, onRemove, onClick, className, ...props },
    ref,
  ) => {
    /*
      The three segments read as one phrase, so they sit on a single `gap-1.5`
      rhythm instead of each carrying its own `px-1.5` — that older padding
      stacked into a 12px trough between every word and made a two-word chip
      as wide as a button. Weight carries the hierarchy: the value is the
      thing you scan for, the field and operator recede.
    */
    const body = (
      <>
        {icon ? (
          <span className="flex items-center [&_svg]:size-3 [&_svg]:text-text-tertiary">
            {icon}
          </span>
        ) : null}
        <span className="text-text-tertiary">{label}</span>
        {operator ? (
          <span className="text-text-tertiary">{operator}</span>
        ) : null}
        <span className="max-w-64 truncate font-medium text-text">{value}</span>
      </>
    );

    const bodyClass = cn(
      "flex h-full items-center gap-1.5 rounded-md",
      onRemove ? "pl-2 pr-1" : "px-2",
    );

    return (
      <div
        ref={ref}
        data-slot="filter-chip"
        className={cn(
          "inline-flex h-6 shrink-0 items-center rounded-md",
          "bg-sunken text-xs text-text",
          "transition-[background-color] duration-100 ease-out-fast",
          "hover:bg-sunken-strong",
          className,
        )}
        {...props}
      >
        {onClick ? (
          <button
            type="button"
            onClick={onClick}
            aria-label={`Edit ${label} filter`}
            className={cn(bodyClass, "focus-visible:outline-none focus-fill")}
          >
            {body}
          </button>
        ) : (
          <span className={bodyClass}>{body}</span>
        )}
        {/*
          A 16px inset target rather than the old full-height slab: hovering
          the remove affordance used to light up a quarter of the chip, which
          read as "the chip is armed" instead of "this X is".
        */}
        {onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${label} filter`}
            className={cn(
              "mr-1 grid size-4 shrink-0 place-items-center rounded-sm",
              "text-text-tertiary",
              "transition-[background-color,color] duration-100 ease-out-fast",
              "hover:bg-hover-strong hover:text-text",
              "focus-visible:outline-none focus-fill",
            )}
          >
            <XIcon aria-hidden="true" className="size-2.5" />
          </button>
        ) : null}
      </div>
    );
  },
);
FilterChip.displayName = "FilterChip";

/* ── GroupByControl / OrderByControl ────────────────────────────────────── */

export interface FilterBarOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

export interface MenuControlProps {
  /** Currently selected option value. */
  value?: string;
  options: FilterBarOption[];
  onChange?: (value: string) => void;
  /** Prefix shown before the selected option, e.g. "Group". */
  label?: string;
  /** Leading glyph. Defaults per control. */
  icon?: React.ReactNode;
  /** Menu heading. Defaults to `label`. */
  menuLabel?: string;
  className?: string;
}

/**
 * Job A — CHOOSE (`docs/picker-taxonomy.md` §5.A A44).
 *
 * "Group by" / "Order by" set a named property of the view from a short, fixed
 * list — that is a value picker, not a command menu, so it is a `Select`. It
 * was a `DropdownMenu` + `DropdownMenuRadioGroup`, which meant the *same job*
 * was done three ways in one app: this control, `data-table-grouping-controls`
 * (a `Select`) and the list-page group-by. The visible tell was the caret —
 * this one had none, because a menu trigger renders no "this opens" glyph.
 *
 * `MenuControlProps` is unchanged, so neither consumer needed an edit.
 */
function MenuControl({
  value,
  options,
  onChange,
  label,
  icon,
  menuLabel,
  className,
}: MenuControlProps) {
  const selected = options.find((option) => option.value === value);

  return (
    <Select value={value} onValueChange={onChange}>
      {/*
        The trigger renders its own value slot rather than `SelectValue`,
        because a filter-bar control reads as "Group <field>" — a prefix plus
        the value, not the value alone. `[&_svg]:size-3` sizes the leading
        glyph to the compact caret the recipe already draws.
      */}
      <SelectTrigger
        variant="compact"
        className={cn("[&_svg]:size-3", className)}
        aria-label={`${label ?? "Options"}${selected ? `: ${selected.label}` : ""}`}
      >
        <span className="flex min-w-0 items-center gap-1.5 truncate">
          {icon}
          {label ? <span className="shrink-0">{label}</span> : null}
          {selected ? (
            <span className="truncate font-medium text-text">
              {selected.label}
            </span>
          ) : null}
        </span>
      </SelectTrigger>
      <SelectContent align="end">
        <SelectGroup>
          <SelectLabel>{menuLabel ?? label}</SelectLabel>
          {options.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              // `FilterBarOption.icon` is caller-supplied and usually carries
              // no size class; a menu item auto-sized it, a `SelectItem` does
              // not. Same 14px as §3.3's menu-icon rule.
              className="[&_svg:not([class*='size-'])]:size-3.5"
            >
              {option.icon}
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

export type GroupByControlProps = MenuControlProps;

/** Compact "Group <field>" dropdown for the right side of a `FilterBar`. */
function GroupByControl({
  label = "Group",
  icon = <LayersIcon aria-hidden="true" />,
  ...props
}: GroupByControlProps) {
  return <MenuControl label={label} icon={icon} {...props} />;
}

export type OrderByControlProps = MenuControlProps;

/** Compact "Order <field>" dropdown for the right side of a `FilterBar`. */
function OrderByControl({
  label = "Order",
  icon = <ArrowUpDownIcon aria-hidden="true" />,
  ...props
}: OrderByControlProps) {
  return <MenuControl label={label} icon={icon} {...props} />;
}

/** Inline check glyph for callers building their own filter popovers. */
function FilterCheck({ className }: { className?: string }) {
  return (
    <CheckIcon
      aria-hidden="true"
      className={cn("size-3.5 text-accent-text", className)}
    />
  );
}

export {
  FilterBar,
  FilterChip,
  AddFilterButton,
  GroupByControl,
  OrderByControl,
  FilterCheck,
};
